import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import type { LeagueLaunch } from '@sorare/marketplace/src/lib/newSeason';

import { FeaturedMainLeagueLaunch } from '#src/components/new-season/FeaturedMainLeagueLaunch/index.tsx';
import { LeagueNavigator } from '#src/components/new-season/LeagueNavigator/index.tsx';
import { MarketTabs } from '#src/components/new-season/MarketTabs/index.tsx';
import { Header } from '#src/components/new-season/NewSeasonHeader/Header/index.tsx';

import type {
  NewSeasonHeader_competition,
  NewSeasonHeader_so5Competition,
} from './__generated__/index.graphql';

const Sticky = styled(Horizontal).attrs({ center: true })`
  position: sticky;
  top: var(--current-stack-height);
  padding: var(--double-unit) 0;
  z-index: 100;
`;
type Props = {
  featured?: LeagueLaunch<
    NewSeasonHeader_competition,
    NewSeasonHeader_so5Competition
  > | null;
  handleLeagueChange: (leagueSlug: string) => void;
  selectedLeague: string;
  upcomingAndLiveLeagues: LeagueLaunch<
    NewSeasonHeader_competition,
    NewSeasonHeader_so5Competition
  >[];
  releasedLeagues: LeagueLaunch<
    NewSeasonHeader_competition,
    NewSeasonHeader_so5Competition
  >[];
  registerPillRef?: (slug: string, element: HTMLElement | null) => void;
};

export const NewSeasonHeader = ({
  featured,
  handleLeagueChange,
  selectedLeague,
  upcomingAndLiveLeagues,
  releasedLeagues,
  registerPillRef,
}: Props) => {
  const isLaptopAndAbove = useIsLaptop();

  if (featured) {
    return (
      <>
        {!isLaptopAndAbove && <MarketTabs />}
        <div className="relative">
          {!!isLaptopAndAbove && (
            <div className="absolute top-0 left-0 z-10">
              <MarketTabs />
            </div>
          )}
          <FeaturedMainLeagueLaunch league={featured} />
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <Sticky gap={0}>
        <LeagueNavigator
          upcomingAndLiveLeagues={upcomingAndLiveLeagues}
          releasedLeagues={releasedLeagues}
          selectedLeague={selectedLeague}
          onNavigateToLeague={handleLeagueChange}
          registerPillRef={registerPillRef}
        />
      </Sticky>
    </>
  );
};

NewSeasonHeader.fragments = {
  competition: gql`
    fragment NewSeasonHeader_competition on Competition {
      slug
      ...FeaturedMainLeagueLaunch_competition
      ...LeagueNavigator_competition
    }
    ${FeaturedMainLeagueLaunch.fragments.competition}
    ${LeagueNavigator.fragments.competition}
  ` as TypedDocumentNode<NewSeasonHeader_competition>,
  so5Competition: gql`
    fragment NewSeasonHeader_so5Competition on So5Competition {
      slug
      ...FeaturedMainLeagueLaunch_so5Competition
    }
    ${FeaturedMainLeagueLaunch.fragments.so5Competition}
  ` as TypedDocumentNode<NewSeasonHeader_so5Competition>,
};
